import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, onSnapshot, query } from "firebase/firestore";
import app from "../../../firebase";
import AdminShell from "../../admin/AdminShell";
import { useSession } from "../../../context/SessionContext";
import { requestAccountDeletion } from "../../../services/accountDeletion";
import { useAccountDeletionRecovery } from "../../../hooks/useAccountDeletionRecovery";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";

const getTextValue = value => {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (Array.isArray(value)) return value.map(getTextValue).filter(Boolean).join(', ');
  if (value && typeof value === 'object') {
    return getTextValue(value.name || value.label || value.title || value.value || value.department || value.program || value.school);
  }
  return '';
};

export default function UserManagement() {
  const navigate = useNavigate();
  const { currentUser } = useSession();
  const {
    pendingDeletions: pendingRecoveryItems,
    recoveryMessage,
    retryPendingDeletions
  } = useAccountDeletionRecovery(currentUser);
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [studentsLoaded, setStudentsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [idSearch, setIdSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [pendingDeletionOperationId, setPendingDeletionOperationId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    setLoading(!(usersLoaded && studentsLoaded));
  }, [usersLoaded, studentsLoaded]);

  useEffect(() => {
    const db = getFirestore(app);
    const usersQuery = query(collection(db, 'users'));
    const studentsQuery = query(collection(db, 'students'));

    const unsubscribeDepartments = onSnapshot(collection(db, 'departments'), snapshot => {
      setDepartments(snapshot.docs.map(item => getTextValue(item.data()?.name)).filter(Boolean));
    }, err => {
      console.error('departments onSnapshot error', err);
      setError(err?.message || 'Failed to load departments');
    });
    const unsubscribeUsers = onSnapshot(usersQuery, snapshot => {
      const data = [];
      snapshot.forEach(userDoc => {
        const userData = userDoc.data() || {};
        const firstName = getTextValue(userData.firstName);
        const middleName = getTextValue(userData.middleName);
        const lastName = getTextValue(userData.lastName);
        data.push({
          ...userData,
          id: userDoc.id,
          origin: 'users',
          fullName: getTextValue(userData.fullName || userData.name) || `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim(),
          email: getTextValue(userData.email),
          role: getTextValue(userData.role),
          department: getTextValue(userData.department || userData.school || userData.program),
          status: getTextValue(userData.status) || 'Active',
        });
      });
      setUsers(data);
      setUsersLoaded(true);
    }, err => {
      console.error('users onSnapshot error', err);
      setError(err?.message || 'Failed to load users');
      setUsersLoaded(true);
    });

    const unsubscribeStudents = onSnapshot(studentsQuery, snapshot => {
      const data = [];
      snapshot.forEach(studentDoc => {
        const studentData = studentDoc.data() || {};
        const fullName = getTextValue(studentData.fullName) ||
          [studentData.firstName, studentData.middleName, studentData.lastName].map(getTextValue).filter(Boolean).join(' ');
        const normalizedCategory = (studentData.category || '').toString().trim().toLowerCase();
        const studentRole = normalizedCategory === 'new student'
          ? 'New Student'
          : normalizedCategory === 'enrolled'
            ? 'Student'
            : studentData.role || studentData.category || 'Student';
        data.push({
          id: `students_${studentDoc.id}`,
          origin: 'students',
          studentDocId: studentDoc.id,
          ...studentData,
          fullName: getTextValue(fullName) || getTextValue(studentData.email) || 'Student',
          email: getTextValue(studentData.email),
          role: getTextValue(studentRole),
          department: getTextValue(studentData.department || studentData.program || studentData.school),
          status: getTextValue(studentData.status) || 'Active',
        });
      });
      setStudents(data);
      setStudentsLoaded(true);
    }, err => {
      console.error('students onSnapshot error', err);
      setError(err?.message || 'Failed to load students');
      setStudentsLoaded(true);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeStudents();
      unsubscribeDepartments();
    };
  }, []);

  const mergedUsers = useMemo(() => {
    return [...users, ...students].sort((a, b) => {
      const nameA = (a.fullName || '').toLowerCase();
      const nameB = (b.fullName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users, students]);

  const filterOptions = useMemo(() => ({
    roles: [...new Set(mergedUsers.map(user => getTextValue(user.role)).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    departments: [...new Set(departments)].sort((a, b) => a.localeCompare(b)),
    statuses: [...new Set(mergedUsers.map(user => getTextValue(user.status)).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
  }), [mergedUsers, departments]);

  const filteredUsers = useMemo(() => {
    const idTerm = idSearch.toLowerCase().trim();
    const nameTerm = nameSearch.toLowerCase().trim();
    return mergedUsers.filter(user => {
      const matchesId = !idTerm || [
        user.id,
        user.uid,
        user.studentDocId,
        user.employeeOrStudentId,
        user.idNumber,
        user.studentId,
      ].some(value => String(value || '').toLowerCase().includes(idTerm));
      const matchesName = !nameTerm || (user.fullName || '').toLowerCase().includes(nameTerm);
      const matchesRole = !roleFilter || getTextValue(user.role).toLowerCase() === roleFilter.toLowerCase();
      const matchesDepartment = !departmentFilter || getTextValue(user.department).toLowerCase() === departmentFilter.toLowerCase();
      const matchesStatus = !statusFilter || getTextValue(user.status).toLowerCase() === statusFilter.toLowerCase();
      return matchesId && matchesName && matchesRole && matchesDepartment && matchesStatus;
    });
  }, [mergedUsers, idSearch, nameSearch, roleFilter, departmentFilter, statusFilter]);

  // pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [idSearch, nameSearch, roleFilter, departmentFilter, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredUsers.length, totalPages]);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, currentPage]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = end - maxButtons + 1;
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const activeCount = useMemo(() => mergedUsers.filter(user => (user.status || '').toLowerCase() === 'active').length, [mergedUsers]);
  const inactiveCount = useMemo(() => mergedUsers.filter(user => (user.status || '').toLowerCase() === 'inactive').length, [mergedUsers]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { targetType, documentId } = deleteTarget;
    setDeleting(true);
    setDeleteMessage('');
    try {
      const result = await requestAccountDeletion({ user: currentUser, targetType, documentId });
      if (result.outcome === 'recovery_pending') {
        setPendingDeletionOperationId(result.operationId || '');
        setDeleteMessage(`${result.error?.message || 'Deletion is incomplete and needs recovery.'}${result.operationId ? ` Operation ID: ${result.operationId}` : ''}`);
      } else {
        setPendingDeletionOperationId('');
        setDeleteMessage(result.outcome === 'record_only'
          ? 'Student record deleted. No linked Authentication account was deleted.'
          : 'Account and profile deleted.');
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete user failed', err);
      setDeleteMessage(err?.message || 'Failed to delete account.');
    } finally {
      setDeleting(false);
    }
  };

  const retryPendingDeletion = async () => {
    if (!pendingDeletionOperationId) return;
    setDeleting(true);
    try {
      const result = await requestAccountDeletion({
        user: currentUser,
        operationId: pendingDeletionOperationId
      });
      if (result.outcome === 'recovery_pending') {
        setDeleteMessage(`${result.error?.message || 'Deletion is still incomplete.'} Operation ID: ${pendingDeletionOperationId}`);
      } else {
        setPendingDeletionOperationId('');
        setDeleteMessage(result.outcome === 'record_only'
          ? 'Student record deleted. No linked Authentication account was deleted.'
          : 'Account and profile deleted.');
      }
    } catch (err) {
      setDeleteMessage(err?.message || 'Deletion recovery failed.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminShell title="User Management">
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, color: '#155c2b' }}>User Management</h2>
            <div style={{ color: '#666', marginTop: 6 }}>Manage system users and access controls.</div>
            <div style={{ color: '#444', marginTop: 6, fontSize: 14 }}>
              Showing {students.length} students and {users.length} users ({mergedUsers.length} total)
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <div style={{ background: '#f8fafb', border: '1px solid #d1d5db', borderRadius: 10, padding: '10px 14px', minWidth: 120 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#155c2b', marginBottom: 4 }}>Active</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{activeCount}</div>
              </div>
              <div style={{ background: '#fff1f3', border: '1px solid #f8c0c7', borderRadius: 10, padding: '10px 14px', minWidth: 120 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#9b1c31', marginBottom: 4 }}>Inactive</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{inactiveCount}</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e6e9ec', borderRadius: 10, padding: '10px 14px', minWidth: 120 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>Total</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{mergedUsers.length}</div>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/users/add')}
            style={{ background: '#155c2b', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
          >
            + Add User
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <div>
            <input
              aria-label="Search by ID"
              placeholder="Search by ID"
              value={idSearch}
              onChange={e => setIdSearch(e.target.value)}
              style={{ boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e6e9ec', width: '100%' }}
            />
          </div>
          <div>
            <input
              aria-label="Search by name"
              placeholder="Search by name"
              value={nameSearch}
              onChange={e => setNameSearch(e.target.value)}
              style={{ boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e6e9ec', width: '100%' }}
            />
          </div>
          <div>
            <select
              aria-label="Filter by role"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              style={{ boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e6e9ec', width: '100%' }}
            >
              <option value="">All Roles</option>
              {filterOptions.roles.map(role => <option key={role} value={role}>{role}</option>)}
            </select>
          </div>
          <div>
            <select
              aria-label="Filter by department"
              value={departmentFilter}
              onChange={e => setDepartmentFilter(e.target.value)}
              style={{ boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e6e9ec', width: '100%' }}
            >
              <option value="">All Departments</option>
              {filterOptions.departments.map(department => <option key={department} value={department}>{department}</option>)}
            </select>
          </div>
          <div>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ boxSizing: 'border-box', padding: 12, borderRadius: 10, border: '1px solid #e6e9ec', width: '100%' }}
            >
              <option value="">All Status</option>
              {filterOptions.statuses.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
        </div>

        {loading && <div style={{ padding: 20, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>Loading users...</div>}
        {error && <div style={{ padding: 16, background: '#fff2f0', borderRadius: 10, color: '#a00', marginBottom: 12 }}>{error}</div>}
        {(recoveryMessage || pendingRecoveryItems.length > 0) && (
          <div role="status" style={{ padding: 16, background: '#fff2f0', borderRadius: 10, color: '#8a1c1c', marginBottom: 12 }}>
            {recoveryMessage || 'A previous account deletion still needs recovery.'}
            <button onClick={retryPendingDeletions} disabled={deleting} style={{ marginLeft: 12 }}>
              Retry pending deletion recovery ({pendingRecoveryItems.length})
            </button>
          </div>
        )}
        {deleteMessage && (
          <div role="status" style={{ padding: 16, background: '#fff2f0', borderRadius: 10, color: '#8a1c1c', marginBottom: 12 }}>
            {deleteMessage}
            {pendingDeletionOperationId && <button onClick={retryPendingDeletion} disabled={deleting} style={{ marginLeft: 12 }}>Retry deletion recovery</button>}
          </div>
        )}
        {!loading && !filteredUsers.length && (
          <div style={{ padding: 24, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontWeight: 700, color: '#155c2b', marginBottom: 8 }}>No users found</div>
            <div style={{ color: '#555' }}>Try a different search term or add a new user.</div>
          </div>
        )}

        {!loading && filteredUsers.length > 0 && (
          <>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900, tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: '#f7faf8', textAlign: 'left', borderBottom: '1px solid #f1f4f3' }}>
                          <th style={{ width: '22%', padding: '14px 16px', fontSize: 13, color: '#555', borderRight: '1px solid #e6e9ec' }}>Name</th>
                  <th style={{ width: '24%', padding: '14px 16px', fontSize: 13, color: '#555', borderRight: '1px solid #e6e9ec' }}>Email</th>
                  <th style={{ width: '16%', padding: '14px 16px', fontSize: 13, color: '#555', borderRight: '1px solid #e6e9ec' }}>Role</th>
                  <th style={{ width: '20%', padding: '14px 16px', fontSize: 13, color: '#555', borderRight: '1px solid #e6e9ec' }}>Department</th>
                  <th style={{ width: '10%', padding: '14px 16px', fontSize: 13, color: '#555', borderRight: '1px solid #e6e9ec' }}>Status</th>
                  <th style={{ width: '8%', padding: '14px 16px', fontSize: 13, color: '#555' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f1f4f3' }}>
                    <td style={{ padding: 14, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', borderRight: '1px solid #f1f4f3' }}>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim()}</td>
                    <td style={{ padding: 14, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', borderRight: '1px solid #f1f4f3' }}>{user.email}</td>
                    <td style={{ padding: 14, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', borderRight: '1px solid #f1f4f3' }}>{user.role}</td>
                    <td style={{ padding: 14, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', borderRight: '1px solid #f1f4f3' }}>{user.department || '—'}</td>
                    <td style={{ padding: 14, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', borderRight: '1px solid #f1f4f3' }}>{user.status || '—'}</td>
                    <td style={{ padding: 14, minWidth: 260 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                          onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dfe7e3', background: '#fff', color: '#155c2b', cursor: 'pointer', flex: '1 1 auto', minWidth: 72 }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/admin/users/reset/${user.id}`)}
                          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dfe7e3', background: '#fff', color: '#155c2b', cursor: 'pointer', flex: '1 1 auto', minWidth: 72 }}
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => {
                            setDeleteMessage('');
                            setDeleteTarget({
                              user,
                              targetType: user.origin === 'students' ? 'student' : 'staff',
                              documentId: user.origin === 'students' ? user.studentDocId : user.id
                            });
                          }}
                          disabled={deleting}
                          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ff6f2e', background: '#fff', color: '#ff6f2e', cursor: 'pointer', flex: '1 1 auto', minWidth: 72 }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e6e9ec', background: currentPage === 1 ? '#f3f4f6' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>{'<'}</button>
            {getPageNumbers().map(p => (
              <button key={p} onClick={() => setCurrentPage(p)} style={{ padding: '6px 10px', borderRadius: 6, border: p === currentPage ? '2px solid #155c2b' : '1px solid #e6e9ec', background: p === currentPage ? '#e6ffed' : '#fff', cursor: 'pointer' }}>{p}</button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e6e9ec', background: currentPage === totalPages ? '#f3f4f6' : '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>{'>'}</button>
          </div>
          </>
        )}
      </div>
      <DeleteConfirmationModal
        open={Boolean(deleteTarget)}
        name={deleteTarget?.user.fullName || deleteTarget?.user.email || "this user"}
        error={deleteMessage}
        deleting={deleting}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteMessage('');
        }}
        onConfirm={handleDelete}
      />
    </AdminShell>
  );
}
